<template>
  <div
    class="home-page min-h-screen overflow-x-hidden"
    :class="isDark ? 'home-page--dark' : 'home-page--light'"
  >
    <!-- Ambient background -->
    <div class="home-bg" aria-hidden="true">
      <div class="home-bg__orb home-bg__orb--1" />
      <div class="home-bg__orb home-bg__orb--2" />
      <div class="home-bg__orb home-bg__orb--3" />
      <div class="home-bg__grid" />
    </div>

    <!-- Nav -->
    <header
      class="home-nav fixed top-0 inset-x-0 z-50 px-4 sm:px-6 py-4"
      :class="{ 'home-nav--visible': pageReady }"
    >
      <div class="home-nav__inner max-w-7xl mx-auto flex items-center justify-between rounded-2xl border backdrop-blur-xl px-5 py-3">
        <div class="flex items-center gap-3">
          <img
            src="https://tiye.ams3.cdn.digitaloceanspaces.com/tiye_assets/TIYE-main-logo.png"
            alt="Tiye"
            class="h-7 w-auto"
            :class="isDark ? 'brightness-0 invert opacity-90' : 'opacity-90'"
          />
          <span class="hidden sm:inline home-nav__divider h-4 w-px" />
          <span class="hidden sm:inline text-xs font-bold tracking-[0.25em] home-nav__brand uppercase">Move</span>
        </div>
        <div class="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            class="home-theme-toggle"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="toggleTheme"
          >
            <span class="material-icons">{{ isDark ? 'light_mode' : 'dark_mode' }}</span>
          </button>
          <router-link to="/login" class="home-btn-ghost">
            Sign in
          </router-link>
        </div>
      </div>
    </header>

    <!-- Hero -->
    <section class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16 lg:pt-32 lg:pb-24">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <!-- Copy -->
        <div class="home-hero-copy">
          <div
            class="home-reveal inline-flex items-center gap-2 px-4 py-2 rounded-full border border-tiye-green/30 bg-tiye-green/10 text-tiye-green text-sm font-medium mb-8"
            style="--delay: 0.1s"
          >
            <span class="home-pulse-dot" />
            For Tiye employees
          </div>

          <h1
            class="home-reveal text-4xl sm:text-5xl xl:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight mb-6"
            style="--delay: 0.2s"
          >
            Where operational
            <span class="home-gradient-text block sm:inline"> work gets done</span>
          </h1>

          <p
            class="home-reveal home-text-muted text-lg sm:text-xl leading-relaxed mb-10 max-w-xl"
            style="--delay: 0.35s"
          >
            Plan launches, track internal projects, and keep the whole team aligned —
            boards, tasks, and assignments built for how Tiye runs day to day.
          </p>

          <div
            class="home-reveal flex flex-col sm:flex-row sm:items-center gap-4 mb-12"
            style="--delay: 0.45s"
          >
            <router-link to="/login" class="home-btn-primary group">
              <span>Sign in to Move</span>
              <span class="material-icons text-lg transition-transform group-hover:translate-x-1">arrow_forward</span>
            </router-link>
            <span class="text-sm home-text-faint">Invite-only · @tiye.services & partners</span>
          </div>

          <!-- Quick stats -->
          <div
            class="home-reveal grid grid-cols-3 gap-4 max-w-md"
            style="--delay: 0.55s"
          >
            <div v-for="stat in stats" :key="stat.label" class="home-stat">
              <span class="home-stat__value">{{ stat.value }}</span>
              <span class="home-stat__label">{{ stat.label }}</span>
            </div>
          </div>
        </div>

        <!-- Animated board preview -->
        <div
          class="home-reveal home-board-wrap"
          style="--delay: 0.3s"
        >
          <div class="home-board-glow" aria-hidden="true" />
          <div class="home-board">
            <div class="home-board__chrome">
              <span class="home-board__dot home-board__dot--r" />
              <span class="home-board__dot home-board__dot--y" />
              <span class="home-board__dot home-board__dot--g" />
              <span class="home-board__title">Operations · Q3</span>
            </div>
            <div class="home-board__columns">
              <div
                v-for="(column, colIndex) in previewColumns"
                :key="column.name"
                class="home-column"
                :style="{ '--col-delay': `${0.6 + colIndex * 0.15}s` }"
              >
                <div class="home-column__head">
                  <span class="home-column__name">{{ column.name }}</span>
                  <span class="home-column__count">{{ column.cards.length }}</span>
                </div>
                <div class="home-column__cards">
                  <div
                    v-for="(card, cardIndex) in column.cards"
                    :key="card.title"
                    class="home-card"
                    :class="`home-card--${card.accent}`"
                    :style="{ '--card-delay': `${0.8 + colIndex * 0.12 + cardIndex * 0.08}s` }"
                  >
                    <p class="home-card__title">{{ card.title }}</p>
                    <div class="home-card__meta">
                      <span class="home-card__tag">{{ card.tag }}</span>
                      <span class="home-card__avatar">{{ card.initials }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features bento -->
    <section class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-24">
      <p
        class="home-reveal home-section-label"
        style="--delay: 0.1s"
      >
        Built for how Tiye works
      </p>
      <h2
        class="home-reveal text-2xl sm:text-3xl font-bold home-text mb-10 max-w-lg"
        style="--delay: 0.15s"
      >
        Everything your team needs to ship operational work
      </h2>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <article
          v-for="(feature, index) in features"
          :key="feature.title"
          class="home-feature home-reveal"
          :style="{ '--delay': `${0.2 + index * 0.1}s` }"
        >
          <div class="home-feature__icon-wrap">
            <span class="material-icons">{{ feature.icon }}</span>
          </div>
          <h3 class="text-lg font-bold home-text mb-2">{{ feature.title }}</h3>
          <p class="home-text-muted text-sm leading-relaxed">{{ feature.description }}</p>
          <div class="home-feature__shine" aria-hidden="true" />
        </article>
      </div>
    </section>

    <!-- CTA band -->
    <section class="relative z-10 px-4 sm:px-6 pb-16">
      <div
        class="home-reveal home-cta max-w-7xl mx-auto rounded-3xl p-10 sm:p-14 flex flex-col sm:flex-row items-center justify-between gap-8 overflow-hidden"
        style="--delay: 0.2s"
      >
        <div class="home-cta__shimmer" aria-hidden="true" />
        <div class="relative">
          <h2 class="text-2xl sm:text-3xl font-bold home-text mb-2">Ready to get moving?</h2>
          <p class="home-text-muted max-w-md">Sign in with your Tiye account and pick up where the team left off.</p>
        </div>
        <router-link to="/login" class="home-btn-primary relative shrink-0">
          <span>Open Move</span>
          <span class="material-icons text-lg">login</span>
        </router-link>
      </div>
    </section>

    <footer class="relative z-10 py-8 text-center text-xs home-text-faint tracking-wide">
      Tiye Move · Internal operations · move.tiye.services
    </footer>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useMoveTheme } from '../composables/useMoveTheme'

export default {
  name: 'HomeView',
  setup () {
    const router = useRouter()
    const store = useStore()
    const pageReady = ref(false)
    const { isDark, toggleTheme } = useMoveTheme()

    const stats = [
      { value: 'Boards', label: 'Per team' },
      { value: 'Live', label: 'Task updates' },
      { value: 'Team', label: 'Collaboration' }
    ]

    const previewColumns = [
      {
        name: 'To do',
        cards: [
          { title: 'Review campaign brief', tag: 'Marketing', initials: 'PM', accent: 'green' },
          { title: 'Update support playbook', tag: 'Ops', initials: 'TK', accent: 'neutral' }
        ]
      },
      {
        name: 'In progress',
        cards: [
          { title: 'App release checklist', tag: 'Product', initials: 'JD', accent: 'amber' },
          { title: 'Partner onboarding docs', tag: 'BD', initials: 'AL', accent: 'green' }
        ]
      },
      {
        name: 'Done',
        cards: [
          { title: 'Weekly ops standup notes', tag: 'Team', initials: 'PP', accent: 'neutral' }
        ]
      }
    ]

    const features = [
      {
        icon: 'view_kanban',
        title: 'Project boards',
        description: 'Organise work into boards and lists — launches, ops, content, or whatever your squad is running.'
      },
      {
        icon: 'task_alt',
        title: 'Tasks & ownership',
        description: 'Assign owners, set due dates and priorities, and see your workload across every board.'
      },
      {
        icon: 'forum',
        title: 'Comments & context',
        description: 'Keep decisions on the task — links, notes, and threads so nothing lives in scattered chats.'
      },
      {
        icon: 'group',
        title: 'Shared with your team',
        description: 'Invite colleagues to boards and run operational work together, not in silos.'
      },
      {
        icon: 'dashboard',
        title: 'Ops at a glance',
        description: 'Dashboard views show board health, open tasks, and what needs attention this week.'
      },
      {
        icon: 'lock',
        title: 'Secure & internal',
        description: 'Powered by Tiye accounts — access managed by your Move administrator.'
      }
    ]

    onMounted(() => {
      if (store.getters['auth/isAuthenticated']) {
        router.push('/dashboard')
        return
      }
      requestAnimationFrame(() => {
        pageReady.value = true
      })
    })

    return { pageReady, isDark, toggleTheme, stats, previewColumns, features }
  }
}
</script>

<style scoped>
/* Home-specific styles — shared shell in move-public-shell.css */
.home-stat {
  padding: 0.75rem 0;
  border-left: 2px solid rgba(0, 225, 129, 0.4);
  padding-left: 0.75rem;
}

.home-stat__value {
  display: block;
  font-size: 0.8rem;
  font-weight: 800;
  color: #00E181;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.home-stat__label {
  display: block;
  font-size: 0.7rem;
  color: var(--home-stat-label);
  margin-top: 0.15rem;
}

/* ── Board preview ── */
.home-board-wrap {
  position: relative;
  perspective: 1200px;
}

.home-board-glow {
  position: absolute;
  inset: -20%;
  background: radial-gradient(ellipse at center, var(--home-board-glow) 0%, transparent 65%);
  animation: glow-pulse 4s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.home-board {
  position: relative;
  border-radius: 1.25rem;
  border: 1px solid var(--home-board-border);
  background: var(--home-board-bg);
  backdrop-filter: blur(20px);
  box-shadow: var(--home-board-shadow), 0 0 0 1px rgba(0, 225, 129, 0.08) inset;
  overflow: hidden;
  animation: board-float 6s ease-in-out infinite;
  transform: rotateY(-4deg) rotateX(2deg);
}

@keyframes board-float {
  0%, 100% { transform: rotateY(-4deg) rotateX(2deg) translateY(0); }
  50% { transform: rotateY(-2deg) rotateX(1deg) translateY(-8px); }
}

.home-board__chrome {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--home-board-chrome-border);
  background: var(--home-board-chrome);
}

.home-board__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.home-board__dot--r { background: #ff5f57; }
.home-board__dot--y { background: #febc2e; }
.home-board__dot--g { background: #28c840; }

.home-board__title {
  margin-left: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--home-board-title);
}

.home-board__columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.65rem;
  padding: 1rem;
  min-height: 280px;
}

.home-column {
  opacity: 0;
  animation: column-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  animation-delay: var(--col-delay);
}

@keyframes column-in {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.home-column__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0 0.15rem;
}

.home-column__name {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--home-column-name);
}

.home-column__count {
  font-size: 0.6rem;
  font-weight: 700;
  color: rgba(0, 225, 129, 0.8);
  background: rgba(0, 225, 129, 0.12);
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
}

.home-column__cards {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.home-card {
  padding: 0.6rem 0.65rem;
  border-radius: 0.65rem;
  border: 1px solid var(--home-card-border);
  background: var(--home-card-bg);
  opacity: 0;
  animation: card-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  animation-delay: var(--card-delay);
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
}

.home-card:hover {
  transform: translateY(-2px) scale(1.02);
  border-color: rgba(0, 225, 129, 0.25);
  box-shadow: var(--home-card-hover-shadow);
}

@keyframes card-in {
  from { opacity: 0; transform: translateY(10px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.home-card--green { border-left: 2px solid #00E181; }
.home-card--amber { border-left: 2px solid #febc2e; }
.home-card--neutral { border-left: 2px solid var(--home-card-neutral); }

.home-card__title {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--home-card-title);
  line-height: 1.35;
  margin-bottom: 0.45rem;
}

.home-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.home-card__tag {
  font-size: 0.55rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--home-card-tag);
}

.home-card__avatar {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: rgba(0, 225, 129, 0.2);
  color: #00E181;
  font-size: 0.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Features ── */
.home-section-label {
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: rgba(0, 225, 129, 0.7);
  margin-bottom: 0.75rem;
}

.home-feature {
  position: relative;
  padding: 1.5rem;
  border-radius: 1.25rem;
  border: 1px solid var(--home-feature-border);
  background: var(--home-feature-bg);
  overflow: hidden;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.35s ease, background 0.35s ease;
}

.home-feature:hover {
  transform: translateY(-4px);
  border-color: rgba(0, 225, 129, 0.25);
  background: var(--home-feature-hover-bg);
}

.home-feature__icon-wrap {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.85rem;
  background: rgba(0, 225, 129, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: transform 0.35s ease, background 0.35s ease;
}

.home-feature:hover .home-feature__icon-wrap {
  transform: scale(1.08) rotate(-3deg);
  background: rgba(0, 225, 129, 0.2);
}

.home-feature__icon-wrap .material-icons {
  font-size: 1.35rem;
  color: #00E181;
}

.home-feature__shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 40%, var(--home-feature-shine) 50%, transparent 60%);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.home-feature:hover .home-feature__shine {
  transform: translateX(100%);
}

/* ── CTA ── */
.home-cta {
  position: relative;
  border: 1px solid var(--home-cta-border);
  background: var(--home-cta-bg);
  transition: background 0.35s ease, border-color 0.35s ease;
}

.home-cta__shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--home-cta-shimmer) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: shimmer 5s ease-in-out infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .home-column,
  .home-card {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }

  .home-board,
  .home-cta__shimmer,
  .home-board-glow {
    animation: none !important;
  }
}

@media (max-width: 1023px) {
  .home-board {
    transform: none;
    animation: board-float-mobile 5s ease-in-out infinite;
  }

  @keyframes board-float-mobile {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
  }
}
</style>
